import { useMemo } from "react";
import { withMetaInfoFields } from "../../Helper/Analytics/tableMetaInfo";
import { usePagesContent } from "../../Hooks/usePagesContent";

/**
 * Entity attributes the Fleet Performance analytics API understands as metaInfo.
 *
 * The spellings are the API's, not ours: licencePlatNumber carries two
 * long-standing typos, and this module says originDepot where BMS Faults says
 * originDepo. Deriving against the wrong list would silently ask for a field the
 * backend ignores, so the list is per module rather than shared.
 */
export const FLEET_PERFORMANCE_META_INFO_FIELDS = [
  "licencePlatNumber",
  "licensePlateNumber",
  "originCountry",
  "originState",
  "originCity",
  "originDepot",
  "deviceID",
  "pingStatus",
  "fleetName",
  "fleetCategory",
];

/**
 * The FleetPerformance org meta keys under metaInfo.FleetPerformance.table.
 *
 * The four *VehiclesTable keys are the drilldown modals' and are named by each
 * chart's own config (FleetChartConfig.js orgMetaTableKeys). The three
 * *Table/-DetailsTable keys below are the tables that are not drilldowns: the
 * aggregated metrics list on the Energy Consumption tab and the vehicle list on
 * each chart-details page. They are collected here so one place shows a
 * customer every table they can configure.
 */
export const FLEET_PERFORMANCE_TABLE_KEYS = {
  ENERGY_CONSUMPTION_METRICS: "energyConsumptionMetricsTable",
  ENERGY_CONSUMPTION_TRENDS_DETAILS: "energyConsumptionTrendsDetailsTable",
  ENERGY_FLOW_TRENDS_DETAILS: "energyFlowTrendsDetailsTable",
};

/**
 * Just the columns an org configured for a table, or null.
 *
 * Kept separate from resolveFleetTableConfig because the metaInfo resolver must
 * derive only from what the ORG asked for. Deriving from the fallback as well
 * would widen every request on an unconfigured org - the fallback's own fields
 * are already in each request's base list by construction.
 */
export const getConfiguredFleetTableColumns = (orgMetaTables, tableKey) =>
  (tableKey ? (orgMetaTables || {})[tableKey] : null)?.columnsConfig || null;

/**
 * A table's columns, from FleetPerformance org meta where the org configures
 * them and from the chart's or module's own config otherwise.
 *
 * Only the columns are configurable. Everything else the caller's config carries
 * - the title, which may be a function of the clicked data point, and the
 * settings toggles - is passed through untouched, so configuring columns cannot
 * blank a heading or take a control away.
 */
/**
 * The order a table renders in, given what the org configured and the caller's own.
 *
 * getColumnsConfig builds columns by walking the ORDER, not the config map, so a
 * column an org adds to columnsConfig without naming in columnOrdering is never
 * built at all. Treating the two as either/or therefore silently swallowed a
 * configured column whenever the ordering was left out - and leaving it out is
 * the right thing to do for a table whose fallback order is computed per data
 * point, like the energy flow trends drilldown, which lists only the metric for
 * the series that was clicked.
 *
 * So the configured order wins when given, and otherwise the caller's order is
 * kept and anything the org configured beyond it is appended. Both halves stay
 * honoured: the per-series order survives and the extra column appears.
 */
const resolveColumnOrder = (columnsConfig, configuredOrder, fallbackOrder) => {
  if (configuredOrder) return configuredOrder;
  if (!columnsConfig) return fallbackOrder;

  const order = fallbackOrder || [];
  const extras = Object.keys(columnsConfig).filter((key) => !order.includes(key));
  return extras.length ? [...order, ...extras] : order;
};

export const resolveFleetTableConfig = (orgMetaTables, tableKey, fallback) => {
  const columnsConfig = getConfiguredFleetTableColumns(orgMetaTables, tableKey);
  if (!columnsConfig) return fallback;

  return {
    ...fallback,
    tableColumnsConfig: columnsConfig,
    tableColumnOrder: resolveColumnOrder(
      columnsConfig,
      (tableKey ? (orgMetaTables || {})[tableKey] : null)?.columnOrdering,
      fallback?.tableColumnOrder
    ),
  };
};

/**
 * The metaInfo a request should ask for: whatever the chart already asked for,
 * plus the entity attributes the columns on show need.
 *
 * Each call site keeps its own base list. They are not identical - the CSV and
 * mail mirrors drop deviceID and pingStatus, which only the on-screen renderers
 * read - and flattening them to one list would quietly widen those requests.
 */
export const buildFleetMetaInfoResolver = (columnsConfig) => (baseFields) =>
  withMetaInfoFields(baseFields, columnsConfig, FLEET_PERFORMANCE_META_INFO_FIELDS);

/**
 * A Fleet Performance table's columns and metaInfo resolver, from org meta where
 * the org configures them and from the module's own constants otherwise.
 *
 * The same wiring the drilldown hook does (Components/FleetChartDrillDown/
 * useFleetChartDrillDown.js), for the tables that are not drilldowns: the two
 * chart-details vehicle lists and the aggregated metrics list.
 *
 * Pass module-level constants as the fallbacks. The result is memoized on their
 * identity, so a literal built during render would hand every render a new
 * config object and reset the caller's selected columns.
 */
export const useFleetTableConfig = (
  tableKey,
  fallbackColumnsConfig,
  fallbackColumnOrder
) => {
  const pagesContent = usePagesContent();
  const orgMetaTables = pagesContent?.metaInfo?.FleetPerformance?.table;

  // Keyed on what this ONE table configured, not on the whole FleetPerformance
  // table block. The block goes undefined -> object when the page meta lands, so
  // depending on it hands back a new resolveMetaInfo to every org that
  // configures ANY Fleet Performance table - including every org already
  // configuring the four drilldowns - and refires the callers' fetch effect for
  // a value that did not change. Both of these stay undefined for an org that
  // configures none of these three keys, so the identity holds across that
  // transition and the request fires once.
  const configuredColumns = getConfiguredFleetTableColumns(orgMetaTables, tableKey);
  const configuredOrdering = (tableKey ? (orgMetaTables || {})[tableKey] : null)
    ?.columnOrdering;

  return useMemo(
    () => ({
      tableColumnsConfig: configuredColumns || fallbackColumnsConfig,
      tableColumnOrder: resolveColumnOrder(
        configuredColumns,
        configuredOrdering,
        fallbackColumnOrder
      ),
      // Wraps a request's own base list. Derived from the org's columns alone,
      // so an unconfigured table keeps its request as it was.
      resolveMetaInfo: buildFleetMetaInfoResolver(configuredColumns),
    }),
    [
      configuredColumns,
      configuredOrdering,
      fallbackColumnsConfig,
      fallbackColumnOrder,
    ]
  );
};
